/* ContractorAccounts.js — «شماره حساب پیمانکار»
   پنجرهٔ مشترک پیمانکاران و قراردادها.
   props: contractorCode, contractorName, contractId (۰ = از پیمانکاران)، contractTitle, autoNew, onClose
   - از پیمانکاران: همهٔ حساب‌های پیمانکار؛ فقط حساب‌های عمومی قابل تغییر
   - از قراردادها: فقط حساب‌های همان قرارداد
   - هر دکمه فقط با مجوز خودش دیده می‌شود؛ سرور هم دوباره بررسی می‌کند */
import React, { useState, useEffect, useCallback } from 'react';
import JalaliDatePicker from './JalaliDatePicker';
import { jsonOf, netErr } from '../utils/http';

const toFa = v => String(v ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = v => String(v ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
const todayFa = () => {
  const p = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date());
  const g = k => (p.find(x => x.type === k) || {}).value || '';
  return `${g('year')}/${g('month')}/${g('day')}`;
};

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const EMPTY = { id: 0, letter_no: '', letter_date: '', account_no: '', branch_code: 0, bank: '' };

function BranchPick({ value, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async t => {
    setQ(t);
    const j = await API(`/contractor-accounts/branches/?q=${encodeURIComponent(toEng(t))}`);
    if (j.ok) { setRows(j.data); setOpen(true); }
  };
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input value={value || q} placeholder="نام بانک، شعبه یا کد شعبه — دابل‌کلیک برای فهرست"
        onChange={e => { onPick({ id: 0, title: '' }); search(e.target.value); }}
        onDoubleClick={() => search('')}
        onKeyDown={e => { if (e.key === 'Delete') { onPick({ id: 0, title: '' }); setQ(''); } if (e.key === 'Escape') setOpen(false); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        style={st.inp} />
      {open && rows.length > 0 && (
        <div style={st.drop}>
          {rows.map(r => (
            <div key={r.id} style={st.dropRow}
              onMouseDown={e => { e.preventDefault(); onPick(r); setOpen(false); setQ(''); }}>
              {toFa(r.code)} — {r.title}
            </div>))}
        </div>)}
    </div>
  );
}

export default function ContractorAccounts({ contractorCode, contractorName, contractId = 0, contractTitle = '',
  autoNew = false, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(null);

  const qs = `contractor=${contractorCode || 0}&contract=${contractId || 0}`;
  const load = useCallback(async () => {
    const j = await API(`/contractor-accounts/?${qs}`);
    if (j.ok) { setData(j); setSel(s => (s && j.data.find(x => x.id === s.id)) || j.data[0] || null); }
    else setErr(j.msg || 'خطا');
    return j;
  }, [qs]);

  useEffect(() => {
    load().then(j => { if (autoNew && j.ok && j.can_new) setForm({ ...EMPTY, letter_date: todayFa() }); });
  }, [load, autoNew]);

  const changed = msg => { setForm(null); load(); onChanged && onChanged(msg); };

  const save = async confirmed => {
    setSaving(true); setErr('');
    const body = JSON.stringify({ ...form, contractor: contractorCode, contract: contractId, confirmed: confirmed ? 1 : 0 });
    const j = form.id
      ? await API(`/contractor-accounts/${form.id}/update/`, { method: 'PUT', body })
      : await API('/contractor-accounts/create/', { method: 'POST', body });
    setSaving(false);
    if (j.needs_confirm) { if (window.confirm(j.msg)) return save(true); return; }
    if (!j.ok) { setErr(j.msg); return; }
    changed(j.msg);
  };

  const del = async r => {
    if (!window.confirm('آيا از حذف مطمئن هستيد؟')) return;
    const j = await API(`/contractor-accounts/${r.id}/delete/?${qs}`, { method: 'DELETE' });
    if (!j.ok) { setErr(j.msg); return; }
    changed(j.msg);
  };

  const sheba = async r => {
    if (!window.confirm('شماره حساب شبا محاسبه شود؟')) return;
    const j = await API(`/contractor-accounts/${r.id}/sheba/`,
      { method: 'POST', body: JSON.stringify({ contractor: contractorCode, contract: contractId }) });
    if (!j.ok) { setErr(j.msg); return; }
    changed(j.msg);
  };

  const pick = r => {
    if (form) return;
    setSel(r); setErr('');
    // حساب ۱۲/۱۳ رقمی بانک دارای شناسهٔ شبا: پیشنهاد محاسبهٔ شبا
    if (r.sheba_calc) sheba(r);
  };

  return (
    <div style={st.overlay} onClick={() => !form && onClose()}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <span>🏦 شماره حساب پیمانکار: {contractorName || data?.owner}
            {contractId ? <span style={{ fontWeight: 400 }}> — قرارداد {toFa(contractTitle)}</span> : null}</span>
          <button onClick={onClose} style={st.close}>✕</button>
        </div>
        <div style={st.body}>
          {err && <div style={st.err}>⚠️ {err}</div>}
          {!data ? <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>⏳ بارگذاری...</div> : (
            <table style={st.table}>
              <thead><tr>
                {['شماره', 'تاریخ', 'حساب', 'خطای حساب شبا', 'بانک', ...(contractId ? [] : ['قرارداد']), '']
                  .map(h => <th key={h} style={st.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {data.data.length === 0 && <tr><td colSpan={7} style={{ ...st.td, textAlign: 'center', color: '#999', padding: 24 }}>
                  حسابی ثبت نشده است</td></tr>}
                {data.data.map((r, i) => (
                  <tr key={r.id} onClick={() => pick(r)}
                    style={{ cursor: 'pointer', background: sel?.id === r.id ? '#d1c4e9' : i % 2 ? '#fff' : '#fff8e1' }}>
                    <td style={st.td}>{toFa(r.letter_no)}</td>
                    <td style={st.td}>{toFa(r.letter_date)}</td>
                    <td style={{ ...st.td, fontFamily: 'monospace', direction: 'ltr' }}>{toFa(r.account_no)}</td>
                    <td style={{ ...st.td, color: '#c62828' }}>{r.sheba_error}</td>
                    <td style={{ ...st.td, textAlign: 'right' }}>{r.bank}</td>
                    {!contractId && <td style={{ ...st.td, textAlign: 'right' }}>{toFa(r.contract)}</td>}
                    <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                      {!form && r.can_edit && <button onClick={e => { e.stopPropagation();
                        setForm({ id: r.id, letter_no: r.letter_no, letter_date: r.letter_date, account_no: r.account_no,
                          branch_code: r.branch_code, bank: r.bank }); }} style={st.mini}>✎</button>}
                      {!form && r.can_delete && <button onClick={e => { e.stopPropagation(); del(r); }}
                        style={{ ...st.mini, background: '#c62828', marginRight: 4 }}>✕</button>}
                      {!r.can_edit && !r.can_delete && <span title={r.scope_note || 'دسترسی ویرایش این حساب را ندارید'}
                        style={{ color: '#bbb' }}>🔒</span>}
                    </td>
                  </tr>))}
              </tbody>
            </table>)}

          {form && (
            <div style={st.form}>
              <div style={{ fontWeight: 700, color: '#4527a0', marginBottom: 8 }}>{form.id ? 'ویرایش حساب' : 'حساب جدید'}</div>
              <div style={st.row}>
                <label style={st.lbl}>شماره نامه
                  <input value={form.letter_no} onChange={e => setForm(f => ({ ...f, letter_no: e.target.value }))} style={st.inp} /></label>
                <label style={st.lbl}>تاریخ نامه
                  <JalaliDatePicker value={form.letter_date} onChange={v => setForm(f => ({ ...f, letter_date: v }))} /></label>
                <label style={st.lbl}>شماره حساب / شبا
                  <input value={form.account_no} dir="ltr" onChange={e => setForm(f => ({ ...f, account_no: e.target.value }))} style={st.inp} /></label>
              </div>
              <div style={st.row}>
                <label style={{ ...st.lbl, flex: 1 }}>بانک
                  <BranchPick value={form.bank} onPick={r => setForm(f => ({ ...f, branch_code: r.id, bank: r.title }))} /></label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { setForm(null); setErr(''); }} style={st.btnGray}>لغو</button>
                <button onClick={() => save(false)} disabled={saving} style={st.btn}>{saving ? '⏳ ...' : '💾 ثبت'}</button>
              </div>
            </div>)}
        </div>
        <div style={st.foot}>
          {data?.can_new && !form && <button onClick={() => setForm({ ...EMPTY, letter_date: todayFa() })} style={st.btn}>＋ جدید</button>}
          <button onClick={onClose} style={st.btnGray}>بستن</button>
        </div>
      </div>
    </div>
  );
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(10,22,40,.55)', zIndex: 100500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', fontFamily: "'Vazirmatn',Tahoma,sans-serif" },
  modal: { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.35)' },
  head: { background: 'linear-gradient(135deg,#4527a0,#5e35b1)', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 14 },
  close: { background: 'rgba(255,255,255,.15)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' },
  body: { padding: 14, overflow: 'auto', flex: 1 },
  foot: { padding: '10px 14px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { background: '#ede7f6', color: '#4527a0', padding: '7px 6px', border: '1px solid #e0e0e0', fontWeight: 700 },
  td: { padding: '6px', border: '1px solid #eee', textAlign: 'center' },
  err: { background: '#fff3f3', border: '1px solid #ffcdd2', color: '#c62828', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13 },
  form: { marginTop: 12, border: '1.5px solid #d1c4e9', borderRadius: 10, padding: 12, background: '#faf8ff' },
  row: { display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' },
  lbl: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#555', flex: '1 1 30%' },
  inp: { padding: '8px 10px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  drop: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 8, maxHeight: 220, overflowY: 'auto', zIndex: 10, boxShadow: '0 6px 20px rgba(0,0,0,.18)' },
  dropRow: { padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3', fontSize: 12 },
  mini: { padding: '3px 10px', background: '#4527a0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 },
  btn: { padding: '7px 18px', background: '#4527a0', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 },
  btnGray: { padding: '7px 18px', background: '#eceff1', color: '#37474f', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 },
};
